<template>
  <div class="reports">

    <header class="masthead">
      <h1 class="title">Statistics<span class="dot">.</span></h1>
    </header>

    <!-- Controls -->
    <div class="panel">
      <div class="tabs">
        <button
          class="tab"
          :class="{ active: mode === 'daily' }"
          @click="setMode('daily')"
        >Daily summary</button>
        <button
          class="tab"
          :class="{ active: mode === 'detail' }"
          @click="setMode('detail')"
        >All events</button>
      </div>

      <div class="fields">
        <label class="field">
          <span class="field-label">From</span>
          <input v-model="from" type="date" class="input">
        </label>

        <label class="field">
          <span class="field-label">To</span>
          <input v-model="to" type="date" class="input">
        </label>

        <!-- Company group. Applied by the backend to badge events AND
             declared statuses, so the rows and the CSV hold only that
             company's people. "All companies" is the old behaviour. -->
        <label class="field">
          <span class="field-label">Company</span>
          <select v-model="companyGroup" class="input select">
            <option :value="0">All companies</option>
            <option v-for="g in companyGroups" :key="g.id" :value="g.id">{{ g.name }}</option>
          </select>
        </label>

        <button class="btn primary" :disabled="loading" @click="run">
          {{ loading ? 'Running…' : 'Run report' }}
        </button>
      </div>
    </div>

    <p v-if="error" class="msg err">{{ error }}</p>

    <div v-else-if="loading" class="loading">
      <span class="pulse"></span>
      <span>Running the report</span>
    </div>

    <template v-else-if="hasRun">
      <div class="result-bar">
        <div class="result-left">
          <input
            v-model="query"
            class="search"
            type="search"
            placeholder="Filter by name or site..."
            aria-label="Filter results"
          >
          <span class="count">
            {{ visible.length }}
            {{ visible.length === 1 ? 'row' : 'rows' }}
            <template v-if="visible.length !== rows.length">
              of {{ rows.length }}
            </template>
          </span>
        </div>

        <button class="btn" :disabled="visible.length === 0" @click="exportCsv">
          Export CSV
        </button>
      </div>

      <p v-if="rows.length === 0" class="msg">No badge events in that range</p>

      <p v-else-if="visible.length === 0" class="msg">No matches</p>

      <div v-else class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th v-for="col in columns" :key="col.key" :class="col.numeric ? 'num' : ''">
                {{ col.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in visible"
              :key="i"
              class="clickable"
              :class="{ incomplete: row.incomplete }"
              @click="openLog(row)"
            >
              <td v-for="col in columns" :key="col.key" :class="col.numeric ? 'num' : ''">
                {{ cell(row, col) }}
                <!-- Beside the name as well as in its own column, so a row
                     that rests partly on an admin's word is seen at a glance. -->
                <span
                  v-if="col.key === 'user_name' && isManual(row)"
                  class="tag-manual"
                >manual</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Unpaired events are flagged rather than guessed at, because the
             alternative is inventing hours nobody actually worked. -->
        <p v-if="incompleteCount > 0" class="flag-note">
          {{ incompleteCount }} highlighted
          {{ incompleteCount === 1 ? 'row has' : 'rows have' }}
          an unmatched entry or exit, so the hours shown are understated.
        </p>
      </div>
    </template>

    <div v-else class="msg">
      Choose date range
    </div>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getDailyReport, getDetailReport, toCsv, downloadCsv } from '@/services/ReportService'
import { getStatusesRange, getStatusTypes, statusMeta } from '@/services/StatusService'
import { getCompanyGroupList } from '@/services/CompanyGroupService'

const router = useRouter()

const DAILY_COLUMNS = [
  // Department is deliberately absent.
  //
  // It used to come from UserTranslation, which is empty now that badges are
  // mapped in BadgeMapping and departments come from Entra. The report resolves
  // people through the badge mapping, which carries a name and an email but no
  // department, so the column was always blank -- on screen and in the export.
  //
  // An always-empty column is worse than no column: it looks like missing data
  // rather than a thing that is not recorded here.
  { key: 'date', label: 'Date' },
  { key: 'user_name', label: 'Name' },
  { key: 'location', label: 'Site' },
  { key: 'first_in', label: 'First in' },
  { key: 'last_out', label: 'Last out' },
  { key: 'total_hours', label: 'Hours', numeric: true },

  // Separate from the badge hours, never added to them.
  //
  // The hours column is what the doors recorded; this is what somebody typed in
  // when they set a status. Both are useful and they are not the same kind of
  // fact, so they sit side by side rather than being summed into one number
  // whose provenance nobody could recover later.
  { key: 'status_label', label: 'Status' },
  { key: 'status_hours', label: 'Declared', numeric: true },
  { key: 'visits', label: 'Visits', numeric: true },
  { key: 'missing_exits', label: 'Missing out', numeric: true },
  { key: 'missing_entries', label: 'Missing in', numeric: true },
  // How many of the day's events an admin typed in. The hours count the
  // same, but the reader should know some rest on an admin's word.
  { key: 'manual_events', label: 'Manual', numeric: true }
]

const DETAIL_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'time', label: 'Time' },
  { key: 'user_name', label: 'Name' },
  { key: 'location', label: 'Site' },
  { key: 'direction', label: 'Direction' },
  { key: 'source', label: 'Source' },
  { key: 'entered_by', label: 'Entered by' },
  { key: 'note', label: 'Reason' }
]

// Default to the last 30 days, which is the range people ask for most often.
function isoDate(d) {
  return d.toISOString().slice(0, 10)
}

const today = new Date()
const monthAgo = new Date()
monthAgo.setDate(monthAgo.getDate() - 30)

const mode = ref('daily')
const from = ref(isoDate(monthAgo))
const to = ref(isoDate(today))

const rows = ref([])
const loading = ref(false)
const error = ref(null)
const hasRun = ref(false)

// Company filter. 0 is everyone. The list is read once; a group added while
// this page is open appears after a reload, which is fine for an admin page.
const companyGroup = ref(0)
const companyGroups = ref([])

onMounted(async () => {
  try {
    companyGroups.value = await getCompanyGroupList()
  } catch (err) {
    // The report still runs unfiltered; only the dropdown is empty.
    console.error(err)
  }
})

const companyGroupName = computed(() =>
  companyGroups.value.find((g) => g.id === companyGroup.value)?.name ?? ''
)
const query = ref('')

const columns = computed(() => (mode.value === 'daily' ? DAILY_COLUMNS : DETAIL_COLUMNS))

const visible = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return rows.value

  return rows.value.filter((row) =>
    // department is gone from the report; searching a field that is never
    // populated only makes the filter look broken.
    [row.user_name, row.location]
      .filter(Boolean)
      .some((f) => String(f).toLowerCase().includes(q))
  )
})

const incompleteCount = computed(() => visible.value.filter((r) => r.incomplete).length)

// Switching report type invalidates the results, since the columns differ.
// Clearing rather than re-running avoids a surprise request on a click that
// looked like a view toggle.
function setMode(next) {
  if (mode.value === next) return
  mode.value = next
  rows.value = []
  hasRun.value = false
}

/**
 * Every date from start to end inclusive, as YYYY-MM-DD.
 *
 * Stepped in UTC so it cannot skip or repeat a day across a daylight-saving
 * boundary, which local-time arithmetic does twice a year.
 */
function datesBetween(start, end) {
  const out = []
  const at = new Date(start + 'T00:00:00Z')
  const last = new Date(end + 'T00:00:00Z')

  if (Number.isNaN(at.getTime()) || Number.isNaN(last.getTime())) return out

  let guard = 0
  while (at <= last && guard++ < 400) {
    out.push(at.toISOString().slice(0, 10))
    at.setUTCDate(at.getUTCDate() + 1)
  }
  return out
}

/** The earlier of two HH:MM times, ignoring blanks. */
function earlier(a, b) {
  if (!a) return b ?? ''
  if (!b) return a
  return a < b ? a : b
}

/** The later of two HH:MM times, ignoring blanks. */
function later(a, b) {
  if (!a) return b ?? ''
  if (!b) return a
  return a > b ? a : b
}

/**
 * What a cell shows.
 *
 * Dates are stored and sent as YYYY-MM-DD, which is right for storage and
 * sorting and reads as a database field on screen. Shown the way the date
 * pickers above the table show it, so the page does not use two formats for
 * the same thing.
 *
 * The CSV export is deliberately NOT changed: a spreadsheet sorts ISO dates
 * correctly and guesses at anything else, and 03/04 means two different days
 * depending on who opens the file.
 */
function cell(row, col) {
  const value = row[col.key]
  if (value === undefined || value === null || value === '') return '\u2014'

  if (col.key === 'date') return formatDate(value)
  // A zero here is "none typed in", which is the normal case and reads
  // better as a dash than as a count.
  if (col.key === 'manual_events' && value === 0) return '\u2014'
  return value
}

/** A daily row with any typed-in event, or a detail row that is one. */
function isManual(row) {
  return row.source === 'manual' || (row.manual_events ?? 0) > 0
}

function formatDate(iso) {
  // Matched rather than split on the separator: splitting "not-a-date" gives
  // three truthy parts and happily prints "date/a/not". Anything that is not
  // an ISO date is passed through untouched.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso))
  if (!match) return iso

  return `${match[3]}/${match[2]}/${match[1]}`
}

function hoursBetween(startTime, endTime) {
  if (!startTime || !endTime) return ''

  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  const mins = (eh * 60 + em) - (sh * 60 + sm)
  if (!(mins > 0)) return ''

  return (mins / 60).toFixed(2)
}

/**
 * Fold declared statuses into the daily rows.
 *
 * A status covering a day a person also badged in attaches to that row. A day
 * they only worked from home has no badge row at all, so one is created -- that
 * is the case that made a remote day look like an absence.
 *
 * Matched on name, which the backend resolves from the badge mapping, because
 * that is what the report lists people by.
 */
function mergeStatuses(report, statusList) {
  const merged = report.map((r) => ({ ...r }))
  const byKey = new Map(merged.map((r) => [`${r.date}|${r.user_name}`, r]))

  for (const entry of statusList ?? []) {
    const name = entry.name || entry.email
    if (!name) continue

    const meta = statusMeta(entry.status)
    const label = meta?.label ?? entry.status
    const declared = hoursBetween(entry.start_time, entry.end_time)

    for (const date of datesBetween(entry.start_date, entry.end_date)) {
      if (date < from.value || date > to.value) continue

      const key = `${date}|${name}`
      const existing = byKey.get(key)

      if (existing) {
        existing.status_label = label
        existing.status_hours = declared

        // The day's span covers declared hours as well as badged ones.
        //
        // Somebody who badged out at 09:06 and then worked from home until
        // 17:00 did not finish at 09:06, and a report saying so would be read
        // as their last activity of the day. Earliest start and latest end
        // across both sources answers "when were they working", which is the
        // question the columns are asking.
        existing.first_in = earlier(existing.first_in, entry.start_time)
        existing.last_out = later(existing.last_out, entry.end_time)
        continue
      }

      // No badge events that day: the row exists only because of the status.
      // The badge columns stay empty rather than showing zero, because no
      // measurement was taken.
      const row = {
        date,
        user_name: name,
        location: '',

        // No badge events at all, so the declared times are the only account of
        // the day there is.
        first_in: entry.start_time ?? '',
        last_out: entry.end_time ?? '',
        total_hours: '',
        visits: '',
        missing_exits: '',
        missing_entries: '',
        status_label: label,
        status_hours: declared
      }

      merged.push(row)
      byKey.set(key, row)
    }
  }

  return merged.sort((a, b) =>
    b.date.localeCompare(a.date) || a.user_name.localeCompare(b.user_name))
}

async function run() {
  loading.value = true
  error.value = null

  try {
    const filters = { from: from.value, to: to.value }
    if (companyGroup.value) filters.company_group = companyGroup.value

    const [report, statusList] = await Promise.all([
      mode.value === 'daily' ? getDailyReport(filters) : getDetailReport(filters),

      // Statuses over the same period, cut by the same company filter: a
      // HuneX report must not list EPOKA people working from home. Only
      // merged into the daily view: the detail view is a list of badge
      // events, and a status is not one.
      //
      // Failing is not fatal; the badge rows still render.
      mode.value === 'daily'
        ? getStatusesRange(from.value, to.value, companyGroup.value).catch(() => [])
        : Promise.resolve([]),

      getStatusTypes().catch(() => [])
    ])

    rows.value = mode.value === 'daily'
      ? mergeStatuses(report, statusList)
      : report

    hasRun.value = true
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

// Opens one person's full log, carrying the current date range across so the
// view does not silently reset to a different period.
function openLog(row) {
  if (!row.user_id) return
  router.push({
    name: 'PersonLog',
    query: { user_id: row.user_id, name: row.user_name, from: from.value, to: to.value }
  })
}

function exportCsv() {
  // Exports what is on screen, filter included, so the file matches what the
  // person is looking at rather than silently containing more.
  const csv = toCsv(columns.value, visible.value)
  // The company goes in the file name, so a HuneX export is not mistaken
  // for everyone's once it has left the page.
  const company = companyGroupName.value
    ? `-${companyGroupName.value.replace(/[^A-Za-z0-9_-]+/g, '_')}`
    : ''
  const name = `vero-attendance-${mode.value}${company}-${from.value}-to-${to.value}.csv`
  downloadCsv(name, csv)
}
</script>


<style scoped>
.reports {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --wash: #f7f9fb;
  --paper: #ffffff;

  /* Left aligned, not centred. Centring made the content drift right of the
     menu and the logo, so nothing lined up with anything else. The max-width
     stays: it stops a line of text running the width of a wide monitor. */
  max-width: 1560px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 28px;
}

.title {
  font-size: clamp(30px, 4.2vw, 44px);
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.05;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

/* ---------- Controls ---------- */

.panel {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
}

.tabs {
  display: flex;
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
}

.tab {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 13px 22px;
  border: none;
  background: var(--paper);
  color: var(--ink-soft);
  cursor: pointer;
}

.tab + .tab { border-left: 1px solid var(--line); }

.tab.active {
  background: var(--ink);
  color: var(--paper);
}

.fields {
  display: flex;
  align-items: flex-end;
  gap: 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.input {
  font-family: inherit;
  font-size: 15px;
  height: 46px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--paper);
  color: #191c1e;
  outline: none;
}

.input:focus { border-color: var(--ink); }

.select { min-width: 190px; }

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  height: 46px;
  padding: 0 22px;
  border: 1px solid var(--ink);
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}

.btn:hover:not(:disabled) { background: var(--ink); color: var(--paper); }

.btn:disabled { opacity: 0.4; cursor: default; }

.btn.primary {
  background: var(--ink);
  color: var(--paper);
}

.btn.primary:hover:not(:disabled) { background: #00548f; }

/* ---------- Result bar ---------- */

.result-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.result-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.search {
  font-family: inherit;
  font-size: 15px;
  height: 46px;
  width: 360px;
  max-width: 100%;
  padding: 0 16px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--paper);
  outline: none;
}

.search:focus { border-color: var(--ink); }

.count {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-soft);
  white-space: nowrap;
}

/* ---------- Table ---------- */

.table-wrap {
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow-x: auto;
  background: var(--paper);
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}

.table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
  background: var(--wash);
  white-space: nowrap;
  position: sticky;
  top: 0;
}

.table td {
  padding: 13px 18px;
  border-bottom: 1px solid #f1f4f7;
  white-space: nowrap;
}

.table tbody tr:last-child td { border-bottom: none; }

.table tbody tr:hover { background: var(--wash); }

.clickable { cursor: pointer; }

/* Numbers right-aligned and tabular so columns of hours can be compared by eye
   rather than read one at a time. */
.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* A day with an unmatched entry or exit is marked, because its hours total is
   necessarily incomplete and should not be read as final.
   
   Marked with a tint of the page's own grey rather than amber: an unmatched tap
   usually means somebody left through a door with no reader, or is still in the
   building. Worth pointing out, because it explains why the total looks low.
   Not a warning. */
.incomplete td {
  background: var(--wash);
}

.incomplete:hover td { background: #eef2f6; }

.flag-note {
  margin: 0;
  padding: 14px 18px;
  border-top: 1px solid var(--line);
  background: var(--wash);
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--ink-soft);
}

/* ---------- States ---------- */

.loading {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 32px 0;
}

.pulse {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--accent);
  animation: pulse 1.1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.2; }
}

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
}

.msg.err { color: #b3261e; }

@media (max-width: 900px) {
  .reports { padding: 0 20px 60px; }
  .panel { flex-direction: column; align-items: stretch; }
  .fields { flex-wrap: wrap; }
  .search { width: 100%; }
  .result-left { flex-direction: column; align-items: stretch; }
}

@media (prefers-reduced-motion: reduce) {
  .pulse { animation: none; }
}

.tag-manual {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 7px;
  border-radius: 5px;
  background: #eef1f4;
  color: var(--ink-soft);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  vertical-align: middle;
}
</style>
